import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Row } from "antd";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import Layout from "../../../components/Layout";
import { customBaseUrl, httpGet } from "../../../store/http";
import { Grid } from "@mui/material";
import { useMatStyles } from "./style";
import { hideLoader, showLoader } from "../../../utils/loader";
import { CSVLink } from "react-csv";
import ReferredUsers from "./referres";
import noDataLogo from "../../../assets/images/no-data.png";
import RefferalWebForm from "./webForm";
import UploadExcel from "./excelUpload";
import ExportData from "./exportData";
import AssignCode from "./assignCode";
import ReferralSettings from "./settings";
import SingleUserBonus from "./singleUserBonus";

const Referrals = () => {
  const { id: referalCode } = useParams();
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
  });
  const styles = useMatStyles();
  const [search] = useState({
    searchBy: "referralUser",
    searchText: "",
  });
  const [allTransfers, setAllTransfers] = useState(0);
  const [allReferrals, setAllreferrals] = useState([]);
  const [filterValue] = useState(false);
  const [isReferrers, setIsReferrers] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [stat, setStat] = useState([]);

  // Missing states identified
  // eslint-disable-next-line no-unused-vars
  const [reversalDays, setReversalDays] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const [showEditDays, setShowEditDays] = useState(false);
  const [showAssignCode, setShowAssignCode] = useState(false);
  const [showMultiple, setShowMultiple] = useState(false);
  const [showExcel, setShowExcel] = useState(false);
  const [showSendSingle, setShowSendSingle] = useState(false);
  const [selectedRow] = useState({});
  const [filteredMeta] = useState(0);

  const getTransactions = useCallback(async (page, size) => {
    setAllreferrals([]);
    showLoader();
    const pages = page ?? pagination.current;
    const limit = size ?? pagination.pageSize;
    let request = await httpGet(
      `${customBaseUrl.referralUrl}/api/v1/admin/fetchAllReferralUsers?field=referralCode&pageNo=${pages + 1}&pageSize=${limit}&value=${referalCode}`
    );
    if (request.status) {
      setAllreferrals(request.data.content || []);
      setAllTransfers(request.data.totalElements || 0);
    }
    hideLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referalCode]);

  const getTemporalReversalDays = useCallback(async () => {
    let request = await httpGet(`${customBaseUrl.walletUrl}/api/v1/config`);
    if (request.status) {
      setReversalDays(request.data);
    }
    hideLoader();
  }, []);

  const getStat = useCallback(async () => {
    let request = await httpGet(`${customBaseUrl.referralUrl}/api/v1/admin/analysis`);
    if (request.status) {
      setStat(request?.data || []);
    }
    hideLoader();
  }, []);

  useEffect(() => {
    getStat();
    getTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStat]);

  const handleChangeRowsPerPage = async (value) => {
    setPagination({ ...pagination, current: 0, pageSize: value });
    await getTransactions(0, value);
  };

  const handleChangePage = async (newPage) => {
    setPagination({ ...pagination, current: newPage });
    await getTransactions(newPage, pagination.pageSize);
  };

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Referrer Name',
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'referenceCode',
      headerName: 'Referrer Referral Code',
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'rcode',
      headerName: 'Referral Referral Code',
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.referralCode : 'N/A'
    },
    {
      field: 'referallname',
      headerName: 'Referral Name',
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral?.name : 'N/A'
    },
    {
      field: 'phoneNumber',
      headerName: 'Referrer Phone',
      width: 250,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'email',
      headerName: "Referrer Email",
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'RphoneNumber',
      headerName: 'Referral Phone',
      width: 250,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.phoneNumber : 'N/A'
    },
    {
      field: 'remail',
      headerName: "Referral Email",
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.email : 'N/A'
    },
    {
      field: 'createdAt',
      headerName: "Referrer Account Creation Date",
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'rcreatedAt',
      headerName: 'Referral Account Creation Date',
      width: 250,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.createdAt : 'N/A'
    },
  ], [styles.tableHeader]);

  const exportData = useMemo(() => allReferrals.map((el) => ({
    name: el.name,
    "Referrer Referral Code": el?.referenceCode,
    "Referral Referral Code": el?.referral ? el?.referral.referralCode : '',
    "Referrer Name": el.name,
    "Referral Name": el?.referral ? el?.referral.name : '',
    "Referrer Phone": el?.phoneNumber?.toString(),
    "Referrer Email": el.email,
    "Referral Phone": el?.referral ? el?.referral.phoneNumber?.toString() : '',
    "referrer Account Creation Date": el.createdAt,
    "referral Account Creation Date": el?.referral ? el?.referral.createdAt : '',
    "Status": el.active,
    'Account Type': el?.corporate ? 'Corporate' : 'Personal',
  })), [allReferrals]);

  const gridStyle = {
    width: "40%",
    textAlign: "left",
    backgroundColor: "#fff",
    padding: "24px 10px 24px 12px",
    fontWeight: "bold",
    marginLeft: "50px",
  };

  const tool = {
    display: "flex",
    borderRadius: "250px",
    justifyContent: "space-between",
    marginBottom: "30px",
  };

  return (
    <Layout>
      <div style={tool}>
        <Row>
          <Button
            variant="contained"
            style={{ marginRight: 20, backgroundColor: !isReferrers ? '#ff6700' : '' }}
            onClick={() => setIsReferrers(false)}
          >
            Aggregators
          </Button>
          <Button
            variant="contained"
            style={{ marginRight: 20, backgroundColor: isReferrers ? '#ff6700' : '' }}
            onClick={() => setIsReferrers(true)}
          >
            Referrals
          </Button>
        </Row>
        <Row type="flex" justify="end" align="middle">
          <Button variant="contained" style={{ marginRight: 20 }}>
            <CSVLink
              data={exportData}
              style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
              filename={`Referrals.csv`}
            >
              DOWNLOAD AS EXCEL
            </CSVLink>
          </Button>
        </Row>
      </div>

      {filterValue ?
        <Card style={{ background: "transparent" }}>
          <Card.Grid style={gridStyle} className="mr-2">
            <p>Referral Code</p>
            <h5 className="text-left">{search?.searchText}</h5>
          </Card.Grid>
          <Card.Grid hoverable={false} style={gridStyle} className="sm-1">
            <p>Total Signup with Referral Code</p>
            <h2 className="text-left">{filteredMeta}</h2>
          </Card.Grid>
        </Card>
        : <Card style={{ background: "transparent" }}>
          <Card.Grid style={gridStyle} className="mr-2">
            <p>Total Referrals</p>
            <h2 className="text-left">{allTransfers ?? 0}</h2>
          </Card.Grid>
        </Card>
      }

      <Grid
        style={{ marginTop: "10px" }}
        container
        spacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 4 }}
      />

      {isReferrers ? <ReferredUsers /> :
        <>
          {allReferrals.length > 0 ? (
            <div style={{ height: 800, width: "100%" }}>
              <DataGrid
                getRowId={row => row.userId || row.id}
                className={styles.table}
                density="comfortable"
                rows={allReferrals}
                columns={columns}
                page={pagination.current}
                paginationMode="server"
                pageSize={pagination.pageSize}
                onPageSizeChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                pagination
                components={{ Toolbar: GridToolbar }}
                rowCount={allTransfers}
              />
            </div>
          ) : (
            <>
              <Row
                type="flex"
                justify="center"
                align="middle"
                className="mt-5"
                style={{ minHeight: "40vh" }}
              >
                <img src={noDataLogo} alt="nodata" style={{ height: "300px" }} />
              </Row>
              <h4 className="mt-3 text-center">No Request yet</h4>
              <p className="mt-3 text-center" style={{ color: "#8C8C8C" }}>
                Request will appear here when you have data
              </p>
            </>
          )}
        </>}

      {showExport && <ExportData showModal={showExport} hideModal={setShowExport} dataList={allReferrals} />}
      {showEditDays && <ReferralSettings getTemporalReversalDays={getTemporalReversalDays} showModal={showEditDays} hideModal={setShowEditDays} title="Edit No of Days" className={styles.modal} />}
      {showAssignCode && <AssignCode showModal={showAssignCode} hideModal={setShowAssignCode} title="Assign Referral Code" className={styles.modal} />}
      {showMultiple && <RefferalWebForm showModal={showMultiple} hideModal={setShowMultiple} />}
      {showExcel && <UploadExcel showModal={showExcel} hideModal={setShowExcel} />}
      {showSendSingle && <SingleUserBonus showModal={showSendSingle} hideModal={setShowSendSingle} selectedData={selectedRow} />}
    </Layout>
  );
};

export default Referrals;